import { classes, media, stylesheet } from 'typestyle';
import { Button, Gray20, White, windowBackdropStyle, WindowHeader } from '@traceair/tds';
import { BREAKPOINT_LARGE } from '../../../layout/styles';
import { useUiStore } from '../../../../store/uiStore';
import React, { useState } from 'react';
import { useAppStore } from '../../../../store/appStore';
import { trackAction } from '../../../../libs/analytics/ActionTracking';
import ReactDatePicker from 'react-datepicker';
import { useFilteredMissions } from '../tabs/useFilteredMissions';
import './custom-react-calendar-styles.css';
import { format } from 'date-fns';
import { getDateHtmlId } from '../helpers/htmlIds';

const css = stylesheet({
  container: {
    flex: '0 0 auto',
    border: `1px solid ${Gray20}`,
    borderRadius: 16,
    width: 318,
    overflow: 'hidden',
    ...media(
      { maxWidth: BREAKPOINT_LARGE },
      {
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        margin: 'auto',
        height: 'fit-content',
        display: 'none',
        zIndex: 3,
        background: White,
      },
    ),
  },
  containerInner: {
    padding: 12,
  },
  header: {
    display: 'none',
    marginBottom: -10,
    ...media(
      { maxWidth: BREAKPOINT_LARGE },
      {
        display: 'flex',
      },
    ),
  },
  backdrop: {
    position: 'fixed',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
    zIndex: 2,
    display: 'none',
    ...media({ maxWidth: BREAKPOINT_LARGE }, { display: 'block' }),
  },
  containerOpen: {
    display: 'block',
  },
  calendar: {
    marginBottom: 8,
  },
});

export const MissionsCalendar = () => {
  const { isCalendarOpen, setIsCalendarOpen } = useUiStore();
  const { pilot } = useAppStore();
  const { sortedDates } = useFilteredMissions();
  const [today] = useState(new Date());

  const handleCloseCalendar = () => setIsCalendarOpen(false);

  const handleFullCalendarClick = () => {
    trackAction('Open full calendar', null);
    window.open(pilot?.calendarLink, '_blank');
  };

  const handleDateChange = (date: Date) => {
    if (date) {
      handleCloseCalendar();
      window.location.hash = getDateHtmlId(format(date, 'yyyy-MM-dd'));
    }
  };

  const filterDate = (date: Date) =>
    availableDates.some((highlightedDate) => {
      const d1 = date.toISOString().split('T')[0];
      const d2 = highlightedDate.toISOString().split('T')[0];
      return d1 === d2;
    });

  const availableDates = sortedDates.map((d) => new Date(d));

  const minDate = new Date(Math.min(...availableDates.map((date) => date.getTime())));
  const maxDate = new Date(Math.max(...availableDates.map((date) => date.getTime())));

  return (
    <>
      {isCalendarOpen && <div className={classes(css.backdrop, windowBackdropStyle)} onClick={handleCloseCalendar} />}
      <div
        className={classes(css.container, isCalendarOpen && css.containerOpen)}
        data-intercom-target="MissionsCalendar"
      >
        <WindowHeader className={css.header} onClose={handleCloseCalendar} theme="light" title="Calendar" />

        <div className={css.containerInner}>
          <div className={classes(css.calendar, 'missions-calendar')}>
            <ReactDatePicker
              inline
              filterDate={filterDate}
              selected={today}
              minDate={minDate}
              maxDate={maxDate}
              onChange={handleDateChange}
              disabledKeyboardNavigation={true}
              fixedHeight
            />
          </div>

          {pilot?.calendarLink && (
            <Button style="secondary" onClick={handleFullCalendarClick} text="Full calendar" fullWidth />
          )}
        </div>
      </div>
    </>
  );
};
